import { ChangeDetectorRef, Injectable, Injector, OnDestroy } from '@angular/core';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ITabItem } from './tabs';
import { ListData } from '@fi-app/components/items-list/list-data.service';
import { TabsModuleService } from '@fi-app/utilities/tabs/tabs-module.service';
import { TransitionOptions } from '@uirouter/core';
import { Subject } from 'rxjs';
import { FiStateChangeService } from '@fi-shared/fi-state-proxy/fi-state-change.service';
import { takeUntil } from 'rxjs/operators';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { isDefined } from '@fi-shared/utils';

export interface ITabsSettings {
    listData: ListData<any>;
    disableAdd?: boolean;
}

interface ITabsOptions {
    replaceLocation?: boolean;
}

@Injectable()
export class TabsModuleBase implements OnDestroy {
    tabs: ITabItem[] = [];

    get tabsSettings() {
        return this.tabsModuleService.tabsSettings;
    }

    selectedIndex = 0;

    protected stateService: FiStateService;
    private stateChangeService: FiStateChangeService;
    protected stateAccessCheckService: StateAccessCheckService;
    protected cdr: ChangeDetectorRef;
    protected tabsModuleService: TabsModuleService;
    private options: ITabsOptions;
    public listData: ListData<any>;

    constructor(injector: Injector, options?: ITabsOptions) {
        this.stateService = injector.get(FiStateService);
        this.stateChangeService = injector.get(FiStateChangeService);
        this.tabsModuleService = injector.get(TabsModuleService);
        this.stateAccessCheckService = injector.get(StateAccessCheckService);
        this.cdr = injector.get(ChangeDetectorRef);
        this.tabsModuleService.resetTabsSettings();
        this.options = options || {};

        this.stateChangeService.onFinish$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            // https://chainformation.atlassian.net/browse/FI-4300
            setTimeout(() => {
                this.updateSelectedTabFromState(true);
                this.initCurrentListDisplayType();
            });
        });

        this.stateChangeService.onCancel$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            // https://chainformation.atlassian.net/browse/FI-4490
            this.updateSelectedTabFromState(false);
        });

        this.tabsModuleService.tabsListData$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((listData: ListData<any>) => {
            this.listData = listData;
            this.cdr.markForCheck();
        });
    }

    onTabIndexChange($event: number) {
        this.tabsModuleService.resetTabsSettings();
        this.selectedIndex = $event;
        const tab = this.tabs[this.selectedIndex];
        if (tab && this.stateService.current.name !== tab.Sref) {
            const options: TransitionOptions = { inherit: false };
            if (this.options.replaceLocation) {
                options.location = 'replace';
            }
            this.stateService.go(tab.Sref, tab.SrefParams, options);
        }
    }

    getAddSref() {
        const addSref = this.tabs[this.selectedIndex].AddState;
        return this.stateAccessCheckService.isStateAllowedForCurrentUser(addSref) ? addSref : null;
    }

    public onAddClick($event): void {
        if (this.canAdd()) {
            this.stateService.go(this.tabs[this.selectedIndex]?.AddState);
        }
    }

    public showMobileFilter() {
        return this.listData && this.tabs[this.selectedIndex]?.showMobileFilter;
    }

    public isMobileMenuShown(): boolean {
        return this.listData && this.tabs[this.selectedIndex]?.showMobileMenu;
    }

    public canAdd() {
        const addState = this.tabs[this.selectedIndex]?.AddState;
        return (
            !this.tabsSettings?.disableAdd &&
            isDefined(addState) &&
            this.stateAccessCheckService.isStateAllowedForCurrentUser(addState)
        );
    }

    protected updateSelectedTabFromState(markForCheck?: boolean) {
        const selectedTabIndex = this.tabs.findIndex((tab) => tab.Sref === this.stateService.current.name);
        if (selectedTabIndex !== -1 && this.selectedIndex !== selectedTabIndex) {
            this.selectedIndex = selectedTabIndex;
            if (markForCheck) {
                this.cdr.markForCheck();
            }
        }
    }

    toggleSideMenu() {
        if (this.listData) {
            this.listData.ToggleSideMenu();
        }
    }

    protected initCurrentListDisplayType() {}

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
